import { useCallback, useEffect, useState } from 'react';
import { useReducedMotion } from '@bliks-io/ui';

// A load that finishes before the loader has been seen for this long never waits for it.
const MIN_VISIBLE_MS = 400;
const MAX_WAIT_MS = 1000;

interface PendingLoader {
  shownAt: number;
  animates: boolean;
  finished: boolean;
  signalDone: () => void;
  waiters: Set<() => void>;
}

const pendingLoaders = new Map<string, PendingLoader>();

function releaseWaiters(loader: PendingLoader) {
  for (const release of [...loader.waiters]) release();
}

/** Registers the loader shown while the route for `processId` loads; returns the unregister call. */
export function registerPendingLoader(
  processId: string,
  { animates, signalDone }: { animates: boolean; signalDone: () => void }
): () => void {
  const loader: PendingLoader = {
    shownAt: Date.now(),
    animates,
    finished: false,
    signalDone,
    waiters: new Set()
  };
  pendingLoaders.set(processId, loader);
  return function unregisterPendingLoader() {
    if (pendingLoaders.get(processId) === loader) pendingLoaders.delete(processId);
    releaseWaiters(loader);
  };
}

export function markPendingLoaderFinished(processId: string): void {
  const loader = pendingLoaders.get(processId);
  if (!loader) return;
  loader.finished = true;
  releaseWaiters(loader);
}

// Hidden tabs pause animations, so nobody would see the finish anyway.
function isWorthFinishing({ animates, shownAt }: { animates: boolean; shownAt: number }): boolean {
  return (
    animates && document.visibilityState !== 'hidden' && Date.now() - shownAt >= MIN_VISIBLE_MS
  );
}

function hasAnimationToFinish(loader: PendingLoader): boolean {
  return !loader.finished && isWorthFinishing(loader);
}

/**
 * Lets a loader that has been on screen for a while finish its animation before the page
 * replaces it. Resolves at once when there is nothing to finish or the load was aborted, and
 * never later than `MAX_WAIT_MS`.
 */
export function waitForPendingLoader(processId: string, signal: AbortSignal): Promise<void> {
  const loader = pendingLoaders.get(processId);
  if (!loader || signal.aborted || !hasAnimationToFinish(loader)) return Promise.resolve();
  return new Promise((resolve) => {
    const release = () => {
      window.clearTimeout(timeout);
      loader.waiters.delete(release);
      signal.removeEventListener('abort', release);
      resolve();
    };
    const timeout = window.setTimeout(release, MAX_WAIT_MS);
    loader.waiters.add(release);
    signal.addEventListener('abort', release);
    loader.signalDone();
  });
}

/**
 * Wires a route pending component into the gate: `done` turns true when the route loader
 * finishes. `PendingProcessLoader` mounts it keyed by `processId`.
 */
export function usePendingLoaderFinish(processId: string) {
  const [done, setDone] = useState(false);
  const animates = !useReducedMotion();

  useEffect(
    function registerWhileShown() {
      return registerPendingLoader(processId, { animates, signalDone: () => setDone(true) });
    },
    [processId, animates]
  );

  const onFinished = useCallback(() => markPendingLoaderFinished(processId), [processId]);
  return { done, onFinished };
}

export type LoadStatus = 'loading' | 'ready' | 'failed';

type LoaderPhase = 'loading' | 'finishing' | 'shown';

interface LoadEpisode {
  status: LoadStatus;
  phase: LoaderPhase;
  shownAt: number;
}

function startEpisode(status: LoadStatus): LoadEpisode {
  return { status, phase: status === 'loading' ? 'loading' : 'shown', shownAt: Date.now() };
}

function nextEpisode(episode: LoadEpisode, status: LoadStatus, animates: boolean): LoadEpisode {
  if (status === 'loading') return startEpisode(status);
  const finishes =
    status === 'ready' &&
    episode.phase === 'loading' &&
    isWorthFinishing({ animates, shownAt: episode.shownAt });
  return { ...episode, status, phase: finishes ? 'finishing' : 'shown' };
}

/**
 * The same finish for a loading gate inside a page: `showLoader` stays true until the loader
 * has played its finish (`done`, then `onFinished`), never longer than `MAX_WAIT_MS`. A load
 * that fails shows its error at once. Key the loader by `episodeKey` so each load grows a
 * fresh process.
 */
export function useLoaderFinish(status: LoadStatus) {
  const animates = !useReducedMotion();
  const [episode, setEpisode] = useState(() => startEpisode(status));

  if (episode.status !== status) setEpisode(nextEpisode(episode, status, animates));

  const finishing = episode.phase === 'finishing';
  const onFinished = useCallback(
    () =>
      setEpisode((current) =>
        current.phase === 'finishing' ? { ...current, phase: 'shown' } : current
      ),
    []
  );

  useEffect(
    function capFinish() {
      if (!finishing) return;
      const timeout = window.setTimeout(onFinished, MAX_WAIT_MS);
      return () => window.clearTimeout(timeout);
    },
    [finishing, onFinished]
  );

  return {
    showLoader: episode.phase !== 'shown',
    done: finishing,
    onFinished,
    episodeKey: episode.shownAt
  };
}
